import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  CurrencyInr, Users, CalendarStar, CheckCircle, Sparkle, Receipt,
  ArrowRight, Warning, ShieldCheck, Buildings, Crown,
} from "@phosphor-icons/react";

const formatINR = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [cadence, setCadence] = useState("monthly");
  const [selectedPack, setSelectedPack] = useState(null);
  const [busy, setBusy] = useState(false);
  const canPay = ["super_admin", "company_admin"].includes(user?.role);

  const load = async () => {
    try {
      const [{ data: s }, { data: inv }] = await Promise.all([
        api.get("/subscription/summary"),
        api.get("/subscription/invoices"),
      ]);
      setSummary(s);
      setInvoices(inv || []);
      if (!selectedPack) setSelectedPack(s.current_pack || "growth");
      if (s.cadence) setCadence(s.cadence);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const activePack = useMemo(
    () => summary?.packs?.find((p) => p.pack === selectedPack),
    [summary, selectedPack],
  );

  const amount = activePack
    ? (cadence === "monthly" ? activePack.monthly_total_inr : activePack.annual_total_inr)
    : 0;

  const startCheckout = async () => {
    if (!canPay) { toast.error("Only admins can subscribe"); return; }
    setBusy(true);
    const ok = await loadRazorpayScript();
    if (!ok) { toast.error("Could not load Razorpay Checkout. Check your network."); setBusy(false); return; }
    try {
      const { data: order } = await api.post("/subscription/create-order", { pack: selectedPack, cadence });
      if (!order.key_id) {
        toast.error("Razorpay keys not configured on the server yet. Please add RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET.");
        setBusy(false); return;
      }
      const rz = new window.Razorpay({
        key: order.key_id,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: "Zenith360 HR",
        description: `${activePack?.pack_name} · ${order.seats} seats · ${cadence}`,
        image: "/zenith360-logo.png",
        theme: { color: "#1a2d59" },
        prefill: {
          name: user?.name || "", email: user?.email || "",
        },
        notes: { pack: selectedPack, cadence, seats: String(order.seats) },
        handler: async (resp) => {
          try {
            await api.post("/subscription/verify-payment", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful! Your pack is active.");
            load();
          } catch (e) {
            toast.error(formatApiError(e));
          }
        },
        modal: {
          ondismiss: () => { setBusy(false); toast.message("Checkout closed. You can resume anytime."); },
        },
      });
      rz.on("payment.failed", (r) => {
        toast.error(`Payment failed: ${r?.error?.description || "Unknown"}`);
      });
      rz.open();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTimeout(() => setBusy(false), 500);
    }
  };

  if (!summary) {
    return <div className="p-8 text-sm text-zinc-500" data-testid="billing-loading">Loading billing…</div>;
  }

  const trialEnd = summary.trial_expiry ? new Date(summary.trial_expiry) : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / (1000 * 60 * 60 * 24))) : null;

  return (
    <div className="max-w-6xl space-y-8" data-testid="billing-page">
      {/* Header */}
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-adb-navy font-bold">Billing & Subscription</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-2 leading-tight">
          {summary.is_agency ? "Agency-wide subscription" : "Your subscription"}
        </h1>
        <p className="text-zinc-600 mt-2 text-sm max-w-2xl">
          Payment is per active user across {summary.is_agency ? "all your client companies" : "your team"}.
          Admin logins are free. Minimum 3 seats billed.
        </p>
      </div>

      {/* Status strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatCard
          testid="billing-status"
          icon={ShieldCheck}
          label="Status"
          value={<span className="capitalize">{summary.billing_status}</span>}
          tone={summary.billing_status === "active" ? "green" : summary.billing_status === "trial" ? "teal" : "magenta"}
        />
        <StatCard
          testid="billing-current-pack"
          icon={Sparkle}
          label="Current pack"
          value={<span className="capitalize">{summary.current_pack}</span>}
        />
        <StatCard
          testid="billing-seat-count"
          icon={Users}
          label="Billable seats"
          value={summary.seat_count}
          subtitle={`min ${summary.min_seats} · admins free`}
        />
        <StatCard
          testid="billing-next-invoice"
          icon={CalendarStar}
          label={summary.billing_status === "trial" ? "Trial ends" : "Next invoice"}
          value={trialEnd && summary.billing_status === "trial"
            ? `${daysLeft} days`
            : (summary.next_invoice_at ? new Date(summary.next_invoice_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—")}
          subtitle={summary.billing_status === "trial" && trialEnd ? trialEnd.toLocaleDateString("en-IN") : ""}
        />
      </div>

      {summary.billing_status === "trial" && daysLeft !== null && daysLeft <= 5 && (
        <div className="border border-zenith-magenta/30 bg-zenith-magenta/5 rounded-sm p-4 flex items-center gap-3" data-testid="billing-trial-nudge">
          <Warning size={20} weight="fill" className="text-zenith-magenta shrink-0" />
          <div className="text-sm">
            <span className="font-semibold text-adb-navy">{daysLeft} days left</span> on your free trial —
            pick a pack below to keep AI, LMS, ATS, and KPI dashboards after {trialEnd?.toLocaleDateString("en-IN")}.
          </div>
        </div>
      )}

      {/* Pack picker */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-bold">Choose your pack</h2>
          <div className="flex items-center gap-1 border border-zinc-200 rounded-sm p-0.5 bg-white" data-testid="billing-cadence-toggle">
            {["monthly", "annual"].map((c) => (
              <button
                key={c}
                data-testid={`billing-cadence-${c}`}
                onClick={() => setCadence(c)}
                className={`px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors ${
                  cadence === c ? "bg-adb-navy text-white" : "text-zinc-500 hover:text-adb-navy"
                }`}
              >
                {c === "monthly" ? "Monthly" : "Annual · save up to 20%"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {summary.packs.map((p) => {
            const active = p.pack === selectedPack;
            const price = cadence === "monthly" ? p.monthly_total_inr : p.annual_total_inr;
            return (
              <button
                key={p.pack}
                type="button"
                data-testid={`billing-pack-${p.pack}`}
                onClick={() => setSelectedPack(p.pack)}
                className={`text-left border rounded-md p-5 transition-all bg-white ${
                  active
                    ? "border-zenith-magenta ring-1 ring-zenith-magenta/20 shadow-sm"
                    : "border-zinc-200 hover:border-adb-navy/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-display text-xl font-bold text-adb-navy">{p.pack_name}</div>
                  {active && <CheckCircle size={18} weight="fill" className="text-zenith-magenta" />}
                </div>
                <div className="mt-3">
                  <div className="text-3xl font-display font-bold text-adb-navy">
                    ₹{formatINR(price)}
                    <span className="text-xs text-zinc-500 font-normal ml-1">/ {cadence === "monthly" ? "month" : "year"}</span>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-1">
                    ₹{p.per_seat_inr}/seat/month × {p.seats} seats
                    {cadence === "annual" && p.annual_discount_percent > 0 && (
                      <span className="ml-1 text-zenith-green font-semibold">
                        · save {p.annual_discount_percent}%
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Summary + CTA */}
      <div className="border border-zinc-200 rounded-md bg-white p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">You'll be charged today</div>
          <div className="font-display text-4xl font-bold text-adb-navy mt-1" data-testid="billing-total-preview">
            ₹{formatINR(amount)}
            <span className="text-sm text-zinc-500 font-normal ml-2">/ {cadence === "monthly" ? "month" : "year"}</span>
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {activePack?.pack_name} · {activePack?.seats} seats · {cadence}
            {" · "}Auto-renews from {new Date().toLocaleDateString("en-IN")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest bg-zenith-green/10 border-zenith-green/40 text-zenith-green">
            UPI · Cards · Netbanking
          </Badge>
          <Button
            data-testid="billing-subscribe-btn"
            onClick={startCheckout}
            disabled={busy || !canPay || amount <= 0}
            className="bg-gradient-to-r from-adb-navy to-zenith-blue text-white hover:brightness-110 h-11 rounded-sm px-6 font-semibold"
          >
            {busy ? "Opening Razorpay…" : (
              <span className="inline-flex items-center gap-2">
                Pay with Razorpay <ArrowRight size={16} />
              </span>
            )}
          </Button>
        </div>
      </div>
      {!canPay && (
        <div className="text-xs text-zinc-500 -mt-4" data-testid="billing-noperm">
          Only <b>super_admin</b> / <b>company_admin</b> can subscribe. Ask your admin.
        </div>
      )}

      {/* Consolidated group / agency breakdown */}
      {summary.member_breakdown?.length > 0 && (
        <div data-testid="billing-member-breakdown">
          <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="font-display text-xl font-bold flex items-center gap-2">
                <Buildings size={18} weight="duotone" className="text-zenith-teal" />
                Seat breakdown by {summary.tenant_type === "group" ? "member company" : "client company"}
              </h2>
              <p className="text-xs text-zinc-500 mt-1">
                Payment rolls up to <span className="font-semibold text-adb-navy">{summary.agency_name}</span> ·
                {" "}{summary.member_count} {summary.member_count === 1 ? "company" : "companies"} in the {summary.tenant_type || "portfolio"} ·
                {" "}Admins are free — only billable users are charged.
              </p>
            </div>
            <Badge variant="outline" className="rounded-sm text-[10px] uppercase tracking-widest bg-zenith-magenta/10 border-zenith-magenta/40 text-zenith-magenta">
              {summary.seat_count} billable seats total
            </Badge>
          </div>
          <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="billing-breakdown-table">
              <thead className="bg-zinc-50 text-left">
                <tr className="text-[11px] uppercase tracking-widest text-zinc-500">
                  <th className="py-2.5 px-3">Company</th>
                  <th className="py-2.5 px-3 text-right">Billable seats</th>
                  <th className="py-2.5 px-3 text-right">Admins (free)</th>
                  <th className="py-2.5 px-3 text-right">Employees</th>
                  <th className="py-2.5 px-3 text-right">Share of bill</th>
                  <th className="py-2.5 px-3 text-right">Subtotal (₹/mo)</th>
                </tr>
              </thead>
              <tbody>
                {summary.member_breakdown.map((m) => {
                  const perSeat = activePack?.per_seat_inr || 0;
                  const subtotal = m.billable_seats * perSeat;
                  const totalBillable = summary.member_breakdown.reduce((s, r) => s + r.billable_seats, 0);
                  const share = totalBillable > 0 ? Math.round((m.billable_seats / totalBillable) * 100) : 0;
                  return (
                    <tr key={m.tenant_id} className="border-t border-zinc-100" data-testid={`billing-breakdown-row-${m.tenant_id}`}>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-2">
                          {m.is_parent && <Crown size={13} weight="fill" className="text-zenith-green shrink-0" />}
                          <div>
                            <div className="font-semibold text-adb-navy">{m.name || "—"}</div>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5">
                              {m.is_parent ? "HQ" : m.tenant_type}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold font-mono-plex">{m.billable_seats}</td>
                      <td className="py-2.5 px-3 text-right text-xs text-zinc-500 font-mono-plex">{m.admin_users}</td>
                      <td className="py-2.5 px-3 text-right text-xs text-zinc-500 font-mono-plex">{m.employees}</td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="inline-flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-zenith-teal to-zenith-magenta" style={{ width: `${share}%` }} />
                          </div>
                          <span className="text-xs text-zinc-500 font-mono-plex w-8 text-right">{share}%</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-right font-semibold font-mono-plex text-adb-navy">₹{formatINR(subtotal)}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-zinc-200 bg-zinc-50/60">
                  <td className="py-2.5 px-3 font-bold text-adb-navy">Total</td>
                  <td className="py-2.5 px-3 text-right font-bold font-mono-plex text-adb-navy" data-testid="billing-breakdown-total-seats">
                    {summary.member_breakdown.reduce((s, r) => s + r.billable_seats, 0)}
                  </td>
                  <td className="py-2.5 px-3 text-right text-xs text-zinc-500 font-mono-plex">
                    {summary.member_breakdown.reduce((s, r) => s + r.admin_users, 0)}
                  </td>
                  <td className="py-2.5 px-3 text-right text-xs text-zinc-500 font-mono-plex">
                    {summary.member_breakdown.reduce((s, r) => s + r.employees, 0)}
                  </td>
                  <td className="py-2.5 px-3 text-right text-[10px] uppercase tracking-widest text-zinc-500">100%</td>
                  <td className="py-2.5 px-3 text-right font-bold font-mono-plex text-adb-navy">
                    ₹{formatINR(summary.member_breakdown.reduce((s, r) => s + r.billable_seats * (activePack?.per_seat_inr || 0), 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">
            Minimum 3 billable seats always apply at the {summary.tenant_type === "group" ? "group" : "agency"} level. Actual invoice uses <code className="font-mono-plex">{summary.seat_count}</code> seats × ₹{activePack?.per_seat_inr}/seat.
          </div>
        </div>
      )}

      {/* Invoices */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-bold">Invoices</h2>
          <div className="text-xs text-zinc-500">Latest 120</div>
        </div>
        {invoices.length === 0 ? (
          <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center text-sm text-zinc-500" data-testid="billing-invoices-empty">
            <Receipt size={22} className="mx-auto text-zinc-400" weight="duotone" />
            <div className="mt-2">No invoices yet — subscribe above to start.</div>
          </div>
        ) : (
          <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="billing-invoices-table">
              <thead className="bg-zinc-50 text-left">
                <tr className="text-[11px] uppercase tracking-widest text-zinc-500">
                  <th className="py-2.5 px-3">Invoice #</th>
                  <th className="py-2.5 px-3">Pack</th>
                  <th className="py-2.5 px-3">Cadence</th>
                  <th className="py-2.5 px-3">Seats</th>
                  <th className="py-2.5 px-3 text-right">Amount</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id} className="border-t border-zinc-100" data-testid={`billing-invoice-row-${i.id}`}>
                    <td className="py-2.5 px-3 font-mono-plex text-xs">{i.invoice_number}</td>
                    <td className="py-2.5 px-3 capitalize">{i.pack}</td>
                    <td className="py-2.5 px-3 capitalize">{i.cadence}</td>
                    <td className="py-2.5 px-3">{i.seats}</td>
                    <td className="py-2.5 px-3 text-right font-semibold">₹{formatINR(i.amount_inr)}</td>
                    <td className="py-2.5 px-3">
                      <span className={`px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-widest border ${
                        i.status === "paid" ? "bg-zenith-green/10 border-zenith-green/40 text-zenith-green"
                        : i.status === "failed" ? "bg-red-50 border-red-200 text-red-700"
                        : "bg-amber-50 border-amber-200 text-amber-700"
                      }`}>
                        {i.status}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-xs text-zinc-500">
                      {i.created_at ? new Date(i.created_at).toLocaleDateString("en-IN") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ testid, icon: Icon, label, value, subtitle, tone = "navy" }) {
  const toneMap = {
    navy: "text-adb-navy",
    green: "text-zenith-green",
    teal: "text-zenith-teal",
    magenta: "text-zenith-magenta",
  };
  return (
    <div data-testid={testid} className="border border-zinc-200 rounded-sm bg-white p-4">
      <div className="flex items-center gap-2">
        <Icon size={16} className={toneMap[tone]} weight="duotone" />
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      </div>
      <div className={`font-display font-bold text-2xl mt-2 ${toneMap[tone]}`}>{value}</div>
      {subtitle && <div className="text-[11px] text-zinc-400 mt-1">{subtitle}</div>}
    </div>
  );
}
